import axios from "axios"
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth0 } from "@auth0/auth0-react"
import { useCustomerData } from "context/customer-provider"
import type {
    WorkItem,
    WorkItemDetail,
    WorkPackageCreateResponse,
    CreateDraftPayload,
    CreateDraftResponse,
    AddItemsResponse,
    RemoveItemResponse,
    DispatchDraftPayload,
} from "types/work-item"

// Create an empty (or pre-seeded) draft work package. This does not fan out to
// recipients — the draft accumulates detections and is dispatched later via the
// draft's dispatch action.
export const useCreateDraft = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: CreateDraftPayload) => {
            const response = await axios.post("work-items", payload)
            return response.data as CreateDraftResponse
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["work-items"] })
        },
    })
}

// The current user's open drafts, newest first (the API returns them in
// created-order; the tray/switcher sorts as needed). Server-filtered to
// work_package + draft + createdByMe.
export const useMyDrafts = (enabled = true) => {
    const { customerId } = useCustomerData()
    return useQuery<WorkItem[]>({
        queryKey: ["work-items", "drafts", customerId],
        queryFn: async () => {
            const response = await axios.get("work-items?type=work_package&status=draft&createdByMe=true")
            return Array.isArray(response.data) ? response.data : []
        },
        enabled: enabled && !!customerId,
    })
}

// Actions on a single draft. Each posts to work-items/{publicId}/actions/{action}
// with the current user's display name/email as fallbacks (the JWT may lack the
// claims — see CLAUDE.md), mirroring WorkPackageModal. dispatch additionally
// carries appBaseUrl so each recipient's magic link picks up the right host.
export const useDraftAction = (publicId: string | null) => {
    const queryClient = useQueryClient()
    const { user } = useAuth0()
    const actorName = user?.name || user?.email || undefined
    const actorEmail = user?.email || undefined
    const appBaseUrl = typeof window !== "undefined" ? window.location.origin : undefined

    const invalidateDraft = () => {
        queryClient.invalidateQueries({ queryKey: ["work-items"] })
        queryClient.invalidateQueries({ queryKey: ["work-item-detail"] })
        // a detection's own work-item list (its detail view) changes when it
        // joins or leaves a draft package
        queryClient.invalidateQueries({ queryKey: ["subject-work-items"] })
    }

    const post = async (action: string, body: Record<string, unknown>) => {
        const response = await axios.post(`work-items/${publicId}/actions/${action}`, { ...body, actorName, actorEmail })
        return response.data
    }

    const addItems = useMutation({
        mutationFn: async (detectionIds: string[]) => (await post("add_items", { detectionIds })) as AddItemsResponse,
        onSuccess: invalidateDraft,
    })

    const removeItem = useMutation({
        mutationFn: async (detectionId: string) => (await post("remove_item", { detectionId })) as RemoveItemResponse,
        onSuccess: invalidateDraft,
    })

    const updateDetails = useMutation({
        mutationFn: async (details: { title?: string; instructions?: string }) => post("update_details", details),
        onSuccess: invalidateDraft,
    })

    const dispatch = useMutation({
        mutationFn: async (payload: DispatchDraftPayload) =>
            (await post("dispatch", { ...payload, appBaseUrl })) as WorkPackageCreateResponse,
        onSuccess: () => {
            // Deliberately NOT invalidating the work-item detail here: the open
            // DraftReview (and its success dialog showing the recipients' magic
            // links) unmounts the moment the detail refetches as non-draft. The
            // dialog's Done button triggers the detail invalidation via
            // onDispatched, once the links have actually been seen.
            queryClient.invalidateQueries({ queryKey: ["work-items"] })
            queryClient.invalidateQueries({ queryKey: ["subject-work-items"] })
            queryClient.invalidateQueries({ queryKey: ["clustered-detections"] })
        },
    })

    return { addItems, removeItem, updateDetails, dispatch }
}

// Shared with the DV2 item-view prefetch so the key can't drift from the hook's.
export const subjectWorkItemsQueryOptions = (customerId: string, subjectType: string | null, subjectId: string | null) =>
    queryOptions<WorkItem[]>({
        queryKey: ["subject-work-items", customerId, subjectType, subjectId],
        queryFn: async () => {
            const response = await axios.get(`subjects/${subjectType}/${subjectId}/work-items`)
            return Array.isArray(response.data) ? response.data : []
        },
        // A prefetched list is used on mount, not fetched again; mutations invalidate it.
        staleTime: 30_000,
    })

export const useWorkItemsForSubject = (subjectType: string | null, subjectId: string | null) => {
    const { customerId } = useCustomerData()
    return useQuery({
        ...subjectWorkItemsQueryOptions(customerId, subjectType, subjectId),
        enabled: !!customerId && !!subjectType && !!subjectId,
    })
}

export const useWorkItemDetail = (publicId: string | null) => {
    const { customerId } = useCustomerData()
    return useQuery<WorkItemDetail>({
        queryKey: ["work-item-detail", customerId, publicId],
        queryFn: async () => {
            const response = await axios.get(`work-items/${publicId}`)
            return response.data
        },
        enabled: !!customerId && !!publicId,
    })
}

export const useMyWorkItems = () => {
    const { customerId } = useCustomerData()
    return useQuery<WorkItem[]>({
        queryKey: ["work-items", "mine", customerId],
        queryFn: async () => {
            const response = await axios.get("work-items?createdByMe=true")
            return Array.isArray(response.data) ? response.data : []
        },
        enabled: !!customerId,
    })
}

// Assignments list with an org-wide ("all") vs personal ("mine") scope. The
// backend list query is already org-wide by default (scoped to the customer);
// createdByMe narrows it to assignments the current user created or is assigned
// to. Org-wide is the default so the whole organisation has visibility.
export const useWorkItems = (filter: "all" | "mine" = "all") => {
    const { customerId } = useCustomerData()
    return useQuery<WorkItem[]>({
        queryKey: ["work-items", "list", customerId, filter],
        queryFn: async () => {
            const response = await axios.get(filter === "mine" ? "work-items?createdByMe=true" : "work-items")
            return Array.isArray(response.data) ? response.data : []
        },
        enabled: !!customerId,
    })
}

// Fetch a fresh magic link for a work item. For a work package the optional
// `email` selects which recipient's link to mint (defaults to the first);
// assignments ignore it.
export const useWorkItemShareLink = () => {
    return useMutation({
        mutationFn: async ({ publicId, email }: { publicId: string; email?: string }) => {
            const response = await axios.post(`work-items/${publicId}/share-link`, {
                appBaseUrl: typeof window !== "undefined" ? window.location.origin : undefined,
                email,
            })
            return response.data as { taskUrl: string }
        },
    })
}

export const useWorkItemAction = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            publicId,
            action,
            payload,
            actorName,
            actorEmail,
        }: {
            publicId: string
            action: string
            payload?: Record<string, unknown>
            actorName?: string
            actorEmail?: string
        }) => {
            const response = await axios.post(`work-items/${publicId}/actions/${action}`, {
                ...(payload || {}),
                actorName,
                actorEmail,
            })
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["work-items"] })
            queryClient.invalidateQueries({ queryKey: ["work-item-detail"] })
            queryClient.invalidateQueries({ queryKey: ["subject-work-items"] })
            queryClient.invalidateQueries({ queryKey: ["notifications"] })
        },
    })
}
